import type { MaybeRefOrGetter, Ref } from 'vue';
import { watch } from 'vue';

import { usePreferredLanguageLoginWatcher } from './usePreferredLanguageLoginWatcher';
import type { UserPreferredLanguage } from './usePreferredLanguage';

type AuthenticatedUser = UserPreferredLanguage & { id: string };

/**
 * Wires login-time language sync and keeps Vuetify's locale aligned with vue-i18n.
 * Used by Helios and Luna `App.vue` setup.
 */
export function useAppAuthenticatedLocale(options: {
  authenticatedUser: MaybeRefOrGetter<AuthenticatedUser | null | undefined>;
  locale: Ref<string>;
  vuetifyLocale: Ref<string>;
  getBaseLanguageCodeFromLocale: (locale: string) => string;
  onLocaleChange?: (locale: string) => void;
}) {
  const {
    authenticatedUser,
    locale,
    vuetifyLocale,
    getBaseLanguageCodeFromLocale,
    onLocaleChange
  } = options;

  function syncVuetifyLocaleFromI18n() {
    vuetifyLocale.value = getBaseLanguageCodeFromLocale(locale.value);
  }

  usePreferredLanguageLoginWatcher(authenticatedUser, {
    onApplied: syncVuetifyLocaleFromI18n
  });

  watch(
    locale,
    newLocale => {
      syncVuetifyLocaleFromI18n();
      onLocaleChange?.(newLocale);
    },
    { immediate: true }
  );
}
